import axiosModule, { AxiosError } from 'axios';

interface APIError {
  error: string;
}

const axios = (
  token?: string,
  contentType: 'application/json' | 'multipart/form-data' = 'application/json'
) => {
  const instance = axiosModule.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL,
    headers: {
      'Content-Type': contentType,
      Accept: 'application/json',
      ...(!!token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : {}),
    },
  });

  return instance;
};

const handleAxiosError = (error: AxiosError | any) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    'errorOccured'
  ).replaceAll('.', '');
};

function isAPIError(e: any): e is APIError {
  return typeof e === 'object' && e !== null && typeof e?.error === 'string';
}

function paramsToQueryString(params: Record<string, any>): string {
  const queryString = Object.entries(params)
    .filter(([_key, value]) => value !== undefined)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map((v: string) => `${encodeURIComponent(`${key}[]`)}=${encodeURIComponent(String(v))}`)
          .join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    })
    .join('&');
  return queryString ? `?${queryString}` : '';
}

/*************************************************
 *                 UUID <-> URI                  *
 *************************************************/

function convertIRIToUuid(iri: string): string {
  if (!iri) return '';

  const uuidRegex =
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/;
  const matches = iri.match(uuidRegex);

  return matches ? matches[0] : '';
}

const IDENTIFIERS = {
  place: '/api/places/',
  user: '/api/users/',
  event: '/api/events/',
  eventTicketType: '/api/event-ticket-types/',
  paymentMethod: '/api/payment-methods/',
  product: '/api/products/',
  serviceTable: '/api/service-tables/',
  productCategories: '/api/product-categories/',
  discount: '/api/discounts/',
  socialMedia: '/api/social-media/',
  eventUserAnswers: '/api/event-user-answers/',
  eventManagerQuestions: '/api/event-manager-questions/',
} as const;

function convertUuidToIRI(
  identifier: keyof typeof IDENTIFIERS,
  uuid: string | number | boolean
) {
  return `${IDENTIFIERS[identifier]}${uuid}`;
}

function replaceUuidsWithIRIs(
  payload: Record<string, string | number | boolean | any>
): Record<string, string | number | boolean> {
  const updatedPayload: Record<string, string | number | boolean> = {
    ...payload,
  };

  for (const key in updatedPayload) {
    if (key in IDENTIFIERS) {
      updatedPayload[key] = convertUuidToIRI(
        key as keyof typeof IDENTIFIERS,
        updatedPayload[key]
      );
    }
  }

  return updatedPayload;
}

export type { APIError };
export {
  axios,
  convertIRIToUuid,
  convertUuidToIRI,
  handleAxiosError,
  isAPIError,
  paramsToQueryString,
  replaceUuidsWithIRIs,
};
